/*
 * Who an email from this conversation goes out as, shared by the reply box and the forward box
 * (docs/2026-11-03-reply-box-mailbox-signature.md): the sending mailbox's name and signature, and
 * the control that adds or takes off that signature, as a mail client's signature control does.
 */
import { useQuery } from "@tanstack/react-query";
import { useId } from "react";
import type { ReplySender } from "../../src/mailbox-reply-signature-contract.ts";
import { EditIcon } from "../icons.tsx";
import { Tip } from "../tip.tsx";
import { inboxApi } from "./api.ts";

/**
 * The sending mailbox's name and signature. Null until read, and where the read fails: the box then
 * names the address alone and offers no signature.
 */
export function useReplySender(workspaceId: string, mailbox: string | null): ReplySender | null {
  const read = useQuery({
    queryKey: ["inbox-v2", "reply-sender", workspaceId, mailbox],
    queryFn: ({ signal }) => inboxApi.replySender(workspaceId, mailbox ?? "", signal),
    enabled: mailbox !== null,
    staleTime: 5 * 60_000,
    retry: false,
  });
  return read.data ?? null;
}

/** The sending mailbox's first name, for "Sending to Jonas as Maya"; null where it is not known. */
export const senderFirstName = (sender: ReplySender | null): string | null =>
  sender?.name?.trim().split(/\s+/u)[0] || null;

const NO_SIGNATURE =
  "This mailbox has no signature in Bison or LeadCue. A workspace admin can add one under Mailboxes.";

/**
 * Add or take off the mailbox's signature. `signed` is whether the words end with it now. A mailbox
 * with none says where one is set, rather than offering nothing.
 */
export function SignatureTool(props: {
  sender: ReplySender | null;
  signed: boolean;
  onAdd: () => void;
  onRemove: () => void;
  frozen: boolean;
  phone: boolean;
}) {
  const { sender } = props;
  const why = useId();
  if (sender === null) return null;
  if (sender.signature === null)
    return (
      <span className="lcr-i2-mail-action lcr-i2-signature-tool">
        <button
          type="button"
          className="lcr-small-button lcr-ghost"
          aria-disabled="true"
          aria-describedby={why}
          onClick={(event) => event.preventDefault()}
        >
          <EditIcon size={16} />
          Signature
        </button>
        <span className="lcr-visually-hidden" id={why}>
          {NO_SIGNATURE}
        </span>
        {!props.phone && <Tip className="lcr-i2-mail-tip">{NO_SIGNATURE}</Tip>}
      </span>
    );
  return (
    <button
      type="button"
      className="lcr-small-button lcr-ghost lcr-i2-signature-tool"
      disabled={props.frozen}
      onClick={props.signed ? props.onRemove : props.onAdd}
    >
      <EditIcon size={16} />
      {props.signed ? "Remove signature" : "Add signature"}
    </button>
  );
}
